import './App.css';
import React, { useState, useRef, useEffect } from 'react';
import LatexComponent from './components/LatexComponent';
import type { TaskProps } from './types/task';
import { parseExpression } from './utils/ParseEquation';
import GenerateFormulas from './utils/GenerateFormulas';
import { formulas } from './data/formulas';
import { generatePDF } from './utils/GeneratePDF';

export default function TaskPageItem({ formula, onComplete, taskType, score, isCompleted, onSaveAnswer, savedAnswer }: TaskProps) {
  const [inputValue, setInputValue] = useState<string>('');

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLocked, setIsLocked] = useState(false);

  const isInputEmpty = inputValue.trim() === '';

  const [announcement, setAnnouncement] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const feedbackRef = useRef<HTMLDivElement>(null);

  const [showDoc, setShowDoc] = useState(false);

  const parsed = parseExpression(inputValue);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);

    onSaveAnswer?.(value);

    if (isSubmitted) setIsSubmitted(false);
    setIsLocked(false);
  };

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter' && ev.ctrlKey) handleSubmit()
  };

  const normalizeExpression = (str: string) => str.toLowerCase().replace(/\s+/g, ''); // ignoririaj razmake i velika mala slova
  const [feedback, setFeedback] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);

  const handleSubmit = () => {
    if (isLocked) return;

    const expected = formula?.latex ?? '';
    const isExact = inputValue.trim().toUpperCase() === expected.trim().toUpperCase();
    const isAlmost = normalizeExpression(inputValue) === normalizeExpression(expected);
    let feedbackMessage = '';
    setIsLocked(true);

    if (isExact) {
      feedbackMessage = 'Bravo! Točan odgovor!';
      setIsCorrect(true);
      onComplete?.(true);
    } else if (isAlmost) {
      feedbackMessage = 'Odgovor je djelomično točan, provjeri razmake u izrazu.';
      setIsCorrect(false);
    } else {
      feedbackMessage = `Netočan odgovor, pokušaj ponovno`;
      setIsCorrect(false);
    }

    setFeedback(feedbackMessage);

    setIsSubmitted(true);
    setTimeout(() => feedbackRef.current?.focus(), 50)
  };

  const handleClear = () => {
    setInputValue('');
    setIsSubmitted(false);
    setAnnouncement('Unos je izbrisan.');
    setTimeout(() => statusRef.current?.focus(), 50);
  };

  const handleShowAnswer = () => {
    const latex = formula?.latex ?? '';
    setInputValue(latex);
    setIsSubmitted(false);
    setAnnouncement('Prikazano je rješenje zadatka.');
    setTimeout(() => statusRef.current?.focus(), 50);
  };

  const openButtonRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showDoc) {
      headingRef.current?.focus();
    }
  }, [showDoc]);

  const closeModal = () => {
    setShowDoc(false);
    openButtonRef.current?.focus();
  };
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (savedAnswer !== undefined) {
      setInputValue(savedAnswer);
    }
  }, [savedAnswer]);

  useEffect(() => {
    if (!showDoc) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [showDoc]);

  useEffect(() => {
    const handleTab = (e: KeyboardEvent) => {
      if (!showDoc) return;

      const focusable = modalRef.current?.querySelectorAll(
        'button, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable || focusable.length === 0) return;

      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [showDoc]);

  function handleUnfocus(e: React.FocusEvent<HTMLElement>) {
    const selection = window.getSelection();
    const range = document.createRange();

    range.selectNodeContents(e.currentTarget);
    selection?.removeAllRanges();
  }

  return (
    <div className="App">
      <div className='box'>
        <div className='container'>
          <div className='left-side'>
            <p tabIndex={0} className='task-description' id="task-description">
              {taskType === 'B'
                ? `Pronađi u dokumentu i kopiraj točan matematički izraz u polje za unos: ${formula.description}`
                : `Napiši matematički izraz koristeći se ispravnom formulom: ${formula.description}`
              }
            </p>
            <textarea
              aria-label='Unesi izraz'
              className='input-area'
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              ref={textareaRef}
            />

            <div className='button-container'>
              <button className='big-button' aria-label="Provjeri uneseni odgovor" onClick={handleSubmit} disabled={isInputEmpty || isCompleted || isLocked}>Provjeri uneseni odgovor</button>
              <button className='big-button' aria-label="Izbriši unos" onClick={handleClear} disabled={isInputEmpty || isCompleted }>Izbriši unos</button>
              <button className='big-button' aria-label="Prikaži rješenje" onClick={handleShowAnswer} disabled={isInputEmpty || isCompleted}>Prikaži rješenje</button>
              {taskType === 'B' && (
                <button className='big-button' ref={openButtonRef} onClick={
                  () => {
                    setShowDoc(true);
                    setAnnouncement('Otvoren je dokument s formulama');
                  }}>
                  Otvori dokument s formulama
                </button>
              )}
            </div>
            {isSubmitted && (
              <div
                className='feedback'
                ref={feedbackRef}
                tabIndex={-1}
                role="alert"
                aria-live="assertive"
              >{feedback}
              </div>
            )}
          </div>
          <div
            tabIndex={-1}
            aria-hidden = "true"
            className='right-side'
          >
            <p tabIndex={-1}
            aria-hidden = "true" className='title'>Uneseni izraz:</p>
            <div className='box2' tabIndex={-1}>
              {isCorrect ? (
                <LatexComponent
                  textInput={parsed.text}
                  equationInput={parsed.eq}
                />
              ) : (null)}
            </div>
          </div>
          {taskType === 'B' && showDoc && (
            <div className="modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby='modal-title'
            >
              <div className="modal-content" ref={modalRef} tabIndex={-1}>
                <button onFocus={handleUnfocus} className="close-button" onClick={closeModal}>Zatvori dokument</button>
                <GenerateFormulas />
                <button onFocus={handleUnfocus} onClick={() => generatePDF(formulas)}>Preuzmi dokument s formulama (PDF)</button>
                <button onFocus={handleUnfocus} className="close-button-bottom" onClick={closeModal}>Zatvori dokument</button>
              </div>
            </div>
          )}
        <div></div>
        </div>
        {isCompleted && (
          <div
            aria-live="polite"        
            className="task-info-completed"
            tabIndex={0}
          >
            <p>Ovaj zadatak je riješen. Tvoj odgovor je spremljen.</p>
          </div>
        )}
        <div aria-live='polite' tabIndex={0}>
          <p>Ostvareni broj bodova: {score ?? 0}</p>
        </div>
        <div
          ref={statusRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          className='sr-only'
        >
          {announcement}
        </div>
      </div>
    </div>
  );
};